import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  usePropertyField,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { cn } from '@vibesites/ui-primitives';
import lifestyleIntroBg from '../assets/lifestyle-intro-bg.svg';
import { useSecondaryTextClass } from '../hooks/useSecondaryTextClass';
import { ctaLinkProps } from '../utils/scheduleTourModal';

const MAPS_API_KEY =
  (typeof import.meta !== 'undefined' && (import.meta as { env?: { GOOGLE_MAPS_API_KEY?: string } }).env?.GOOGLE_MAPS_API_KEY) ||
  '';

const exploreCtaDef: SlotGroupDefinition = {
  label: 'Explore Neighborhood CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/neighborhood' },
    label: { label: 'Label', type: 'text', defaultValue: 'EXPLORE THE NEIGHBORHOOD' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

import { btnOnLight } from '../utils/buttonStyles';

const btnClassName =
  `inline-flex items-center justify-center min-w-[11rem] px-8 py-3.5 font-body text-sm font-medium tracking-[0.18em] uppercase ${btnOnLight}`;
function buildMapSrc(lat: string | null, lng: string | null, address: string | null): string {
  const hasCoords = !!(lat && lng && lat.trim() !== '' && lng.trim() !== '');
  if (hasCoords && MAPS_API_KEY) {
    return `https://www.google.com/maps/embed/v1/place?key=${MAPS_API_KEY}&q=${lat},${lng}&zoom=12`;
  }
  if (hasCoords) {
    return `https://maps.google.com/maps?q=${lat},${lng}&z=12&output=embed`;
  }
  if (address && address.trim() !== '') {
    return `https://maps.google.com/maps?q=${encodeURIComponent(address.trim())}&z=12&output=embed`;
  }
  return '';
}

export function NeighborhoodDiscover() {
  const exploreCta = useSlotGroup('neighborhooddiscover.explore_cta', exploreCtaDef);
  const secondaryTextClass = useSecondaryTextClass();

  const lat = usePropertyField('property_address_latitude') as string | null;
  const lng = usePropertyField('property_address_longitude') as string | null;
  const address = usePropertyField('property_full_address') as string | null;
  const mapSrc = buildMapSrc(lat, lng, address);

  return (
    <section
      className="neighborhood-discover relative overflow-hidden py-16 md:py-24 px-4 sm:px-6 bg-background bg-cover bg-center bg-no-repeat"
      style={{ backgroundImage: `url(${lifestyleIntroBg})` }}
    >
      <div className="relative z-[1] mx-auto max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          <div className="text-left">
            <RichTextContent
              path="neighborhooddiscover.headline"
              as="h2"
              className={cn(
                'font-body text-2xl sm:text-3xl md:text-4xl font-medium tracking-[0.12em] uppercase leading-tight',
                secondaryTextClass,
              )}
            />

            <RichTextContent
              path="neighborhooddiscover.body"
              as="div"
              className="font-body mt-6 md:mt-8 max-w-xl text-sm sm:text-base text-foreground leading-relaxed [&_a]:text-primary [&_a]:no-underline hover:[&_a]:underline"
            />

            <div className="mt-8 md:mt-10">
              <EditableSlotGroup
                groupPath="neighborhooddiscover.explore_cta"
                definition={exploreCtaDef}
              >
                <a
                  {...ctaLinkProps(exploreCta.url || '/neighborhood', exploreCta._target)}
                  className={btnClassName}
                >
                  {exploreCta.label || 'EXPLORE THE NEIGHBORHOOD'}
                </a>
              </EditableSlotGroup>
            </div>
          </div>

          <div className="w-full aspect-square overflow-hidden">
            {mapSrc ? (
              <iframe
                className="block w-full h-full border-0"
                src={mapSrc}
                title="Neighborhood map"
                loading="lazy"
                allowFullScreen
                referrerPolicy="no-referrer-when-downgrade"
              />
            ) : (
              <div className="flex h-full w-full items-center justify-center bg-muted">
                <p className="font-body text-sm text-muted-foreground">Map location not available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'NeighborhoodDiscover',
  displayName: 'Neighborhood Discover',
  tokens: {
    'neighborhooddiscover.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '<p>DISCOVER THE NEIGHBORHOOD</p>',
    },
    'neighborhooddiscover.body': {
      label: 'Body',
      type: 'richtext',
      defaultField: 'property_home_neighborhood_blurb',
      defaultValue:
        '<p><span data-type="field-token" data-field="property_name"></span> is situated in a quiet neighborhood near excellent schools, parks, and everyday conveniences. <span data-type="field-token" data-field="property_address_city"></span> is the perfect place to call home — known for its peaceful ambiance, rich history, and easy access to dining and entertainment. Explore the interactive map to get to know our neighborhood.</p>',
      generator: {
        prompt:
          'Write a warm neighborhood introduction for this property. Mention the property name and city naturally. Highlight nearby landmarks, schools, dining, outdoor spaces, or historic districts that actually exist for this location — do not invent places. Close by inviting the reader to explore the interactive map.',
        context:
          'Home page section below Lifestyle Intro: left column has a blue uppercase headline ("DISCOVER THE NEIGHBORHOOD") and an outline "Explore the Neighborhood" button; right column is an interactive Google Map.',
        length: '1 paragraph, 70–110 words',
      },
    },
  },
  slotGroups: {
    'neighborhooddiscover.explore_cta': exploreCtaDef,
  },
};

(NeighborhoodDiscover as unknown as { contentSchema: ComponentContentSchema }).contentSchema =
  contentSchema;

export default NeighborhoodDiscover;
